import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "dangerouslyForceFocusStyling", "dangerouslyForceHoverStyling", "defaultOption", "defaultValue", "disabled", "error", "errorMessage", "helpMessage", "id", "label", "multiple", "options", "tooltip", "tooltipPlacement", "value"];
import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { useId } from '@nerdwallet/react-accessibility';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { InputGroup, shouldRenderInputGroup } from '../InputGroup/InputGroup';
import { Multiselect } from './Multiselect';
import { NativeSelect } from './NativeSelect';
import { COARSE_POINTER, getPointerType } from './pointer-utils';
import { SELECT_PROP_TYPES, SELECT_DEFAULT_PROPS } from './prop-types';
/**
 * Renders a select component as a native or dropdown style.
 */

export var Select = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var className = props.className,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      defaultOption = props.defaultOption,
      defaultValue = props.defaultValue,
      disabled = props.disabled,
      error = props.error,
      errorMessage = props.errorMessage,
      helpMessage = props.helpMessage,
      idProp = props.id,
      label = props.label,
      multiple = props.multiple,
      options = props.options,
      tooltip = props.tooltip,
      tooltipPlacement = props.tooltipPlacement,
      _props$value = props.value,
      value = _props$value === void 0 ? '' : _props$value,
      otherProps = _objectWithoutProperties(props, _excluded);

  useDeprecatedPropsWarning({
    component: 'Select',
    deprecatedProps: [{
      version: '10.5.12',
      deprecated: 'multiple',
      migration: 'Use <CheckboxGroup> to support selecting multiple items.'
    }],
    props: props
  });
  useDeprecatedClassNameStylePropsWarning('Select', {
    className: className,
    style: otherProps.style
  });
  var id = useId(idProp);
  var errorMessageId = "".concat(id, "-error-message");
  var helpMessageId = "".concat(id, "-help-message");
  var ariaDescribedByIds = "".concat(errorMessageId, " ").concat(helpMessageId); // We can only attach an `aria-describedby` attribute if at least one of
  // the things it references is actually being displayed.
  // See https://nerdwallet.atlassian.net/browse/FEI-2206

  var renderAriaDescribedBy = helpMessage || errorMessage && error; // Native is assumed better UI for coarsely-pointed devices (touch, switch, etc.)

  var DropdownSelectIfBest = getPointerType() === COARSE_POINTER ? NativeSelect : Multiselect; // Always default to native for this case

  var SelectComponent = multiple ? DropdownSelectIfBest : NativeSelect;
  var willRenderInputGroup = shouldRenderInputGroup(props);
  var selectOptions = useMemo(function () {
    // Don't add "Pick one" if `options` already has an empty string value,
    // to avoid creating a conflicting/duplicate option.
    if (!defaultOption || options !== null && options !== void 0 && options.some(function (option) {
      return option.value === '';
    })) {
      return options;
    }

    return [{
      value: '',
      label: 'Pick one',
      disabled: true
    }].concat(_toConsumableArray(options));
  }, [defaultOption, options]);
  var select = /*#__PURE__*/React.createElement(SelectComponent, _extends({
    "aria-describedby": renderAriaDescribedBy ? ariaDescribedByIds : undefined,
    "aria-invalid": error ? 'true' : undefined,
    "data-currency": "Select",
    id: id,
    innerRef: ref,
    className: classNames(_defineProperty({}, className, className && !willRenderInputGroup)),
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling,
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling,
    disabled: disabled,
    error: error,
    multiple: multiple,
    options: selectOptions,
    value: value
  }, otherProps));

  if (!willRenderInputGroup) {
    return select;
  }

  return /*#__PURE__*/React.createElement(InputGroup, {
    className: className,
    disabled: disabled,
    error: error,
    errorMessage: errorMessage,
    errorMessageId: errorMessageId,
    helpMessage: helpMessage,
    helpMessageId: helpMessageId,
    input: select,
    inputId: id,
    label: label,
    tooltip: tooltip,
    tooltipPlacement: tooltipPlacement
  });
});
Select.displayName = 'Select';

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  Select.propTypes = _objectSpread({
    /** An array of options `[{ value, label }]`. See example. */
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.node,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      disabled: PropTypes.bool,

      /** Silence the maxLineLength error to allow a label > 21 characters. Generally only used in instances where copy or compliance requires them. */
      __dangerouslyAllowLongLabel: PropTypes.bool
    })),

    /** The currently selected value. Must match a provided option. */
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.bool, PropTypes.number, PropTypes.string]),

    /** When true, the default option will be "Pick one" instead of the first option */
    defaultOption: PropTypes.bool,

    /** Show the select in an error visual state. */
    error: PropTypes.bool,

    /** Optional label for the select. */
    label: PropTypes.string,

    /** Optional text to be shown indicating the user is in an error state. */
    errorMessage: PropTypes.node,

    /** Optional help text shown below the input. */
    helpMessage: PropTypes.node,

    /** Optional tooltip shown via an icon next to the label. */
    tooltip: PropTypes.node,

    /** Positioning of tooltip. */
    tooltipPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),

    /** When true, sets width to 100% on the input. */
    fullWidth: PropTypes.bool,

    /** Shows a disabled style. */
    disabled: PropTypes.bool,

    /**
     * Allows selecting multiple options.
     * @deprecated
     */
    multiple: PropTypes.bool,

    /** Id attribute for select element. */
    id: PropTypes.string.isRequired,

    /** Name attribute for select element. */
    name: PropTypes.string,

    /** Value change callback. */
    onChange: PropTypes.func,

    /** Callback on close, browser mutliselect only */
    onClose: PropTypes.func,

    /** Focus callback. */
    onFocus: PropTypes.func,

    /** Blur callback. */
    onBlur: PropTypes.func,

    /** Callback on open, browser mutliselect only */
    onOpen: PropTypes.func,

    /**
     * The initial value when state is uncontrolled. Must match a provided
     * option.
     */
    defaultValue: PropTypes.string,

    /**
     * Class on the <input> element.
     *
     * @deprecated
     */
    className: PropTypes.string,

    /** Force the component to use its focus styles. */
    dangerouslyForceFocusStyling: PropTypes.bool,

    /** Force the component to use its hover styles. */
    dangerouslyForceHoverStyling: PropTypes.bool
  }, SELECT_PROP_TYPES);
}

Select.defaultProps = _objectSpread({
  label: null,
  errorMessage: null,
  helpMessage: null,
  tooltip: null,
  tooltipPlacement: null
}, SELECT_DEFAULT_PROPS);